/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * FormInput — Premium Input with Animated Focus States
 * ============================================================================
 *
 * Features: brand-accent focus ring, animated icon tint, floating focus bar,
 * password toggle with rotation, error/success pop-in, cubic-bezier
 * transitions, prefers-reduced-motion support.
 *
 * ============================================================================
 */

import React, { useState, forwardRef } from 'react';
import { Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  success?: boolean;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      label,
      error,
      success,
      helperText,
      leftIcon,
      rightIcon,
      fullWidth = true,
      type = 'text',
      className = '',
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const isPassword = type === 'password';
    const inputType = isPassword ? (showPassword ? 'text' : 'password') : type;

    return (
      <>
        <style>{fiStyles}</style>
        <div className={`fi-root ${fullWidth ? 'fi-full' : ''}`}>
          {/* Label */}
          <label className="fi-label">{label}</label>

          {/* Input wrapper */}
          <div
            className={`
              fi-wrap
              ${isFocused ? 'fi-focused' : ''}
              ${error ? 'fi-error' : ''}
              ${success ? 'fi-success' : ''}
              ${props.disabled ? 'fi-disabled' : ''}
            `}
          >
            {/* Focus accent bar — slides in from left */}
            <div className="fi-accent" />

            {/* Left icon */}
            {leftIcon && (
              <span className="fi-icon fi-icon-left">
                <span className="fi-icon-inner">{leftIcon}</span>
              </span>
            )}

            {/* Input element */}
            <input
              ref={ref}
              type={inputType}
              placeholder={label}
              className={`fi-input ${leftIcon ? 'fi-has-left' : ''} ${className}`}
              onFocus={(e) => {
                setIsFocused(true);
                props.onFocus?.(e);
              }}
              onBlur={(e) => {
                setIsFocused(false);
                props.onBlur?.(e);
              }}
              {...props}
            />

            {/* Right side: password toggle / icon / status */}
            {isPassword ? (
              <button
                type="button"
                className="fi-pwd-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <span className={`fi-pwd-icon ${showPassword ? 'fi-pwd-visible' : ''}`}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </span>
              </button>
            ) : rightIcon ? (
              <span className="fi-icon fi-icon-right">{rightIcon}</span>
            ) : error ? (
              <span className="fi-icon fi-icon-right fi-status-error">
                <AlertCircle size={16} />
              </span>
            ) : success ? (
              <span className="fi-icon fi-icon-right fi-status-success">
                <CheckCircle size={16} />
              </span>
            ) : null}
          </div>

          {/* Error or helper text */}
          {(error || helperText) && (
            <p className={`fi-message ${error ? 'fi-msg-error' : ''}`}>
              {error || helperText}
            </p>
          )}
        </div>
      </>
    );
  }
);

FormInput.displayName = 'FormInput';

/* ─── Styles ─── */
const fiStyles = `
  /* ── Root ── */
  .fi-root {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .fi-full { width: 100%; }

  /* ── Label ── */
  .fi-label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--color-text-secondary);
    transition: color 200ms ease;
  }

  /* ── Wrapper ── */
  .fi-wrap {
    position: relative;
    display: flex;
    align-items: center;
    background: var(--color-bg-tertiary);
    border: 1.5px solid var(--color-border-default, rgba(255,255,255,0.08));
    border-radius: 3px;
    transition: border-color 200ms cubic-bezier(0.16,1,0.3,1),
                box-shadow 200ms cubic-bezier(0.16,1,0.3,1),
                background 200ms ease;
    overflow: hidden;
  }

  [data-theme="light"] .fi-wrap {
    background: #FFFFFF;
    border-color: #CBD5E0;
  }

  .fi-wrap:hover:not(.fi-disabled):not(.fi-error) {
    border-color: var(--color-border-strong, rgba(255,255,255,0.18));
    background: var(--color-bg-hover);
  }

  [data-theme="light"] .fi-wrap:hover:not(.fi-disabled):not(.fi-error) {
    border-color: #94A3B8;
    background: #F8FAFC;
  }

  .fi-wrap.fi-focused {
    border-color: var(--color-brand-primary);
    box-shadow: 0 0 0 3px var(--color-brand-glow, rgba(139,0,0,0.15));
    background: var(--color-bg-tertiary);
  }

  [data-theme="light"] .fi-wrap.fi-focused {
    border-color: var(--color-brand-primary);
    box-shadow: 0 0 0 3px var(--color-brand-glow, rgba(26,95,122,0.12));
    background: #FFFFFF;
  }

  .fi-wrap.fi-error {
    border-color: var(--color-error);
  }
  .fi-wrap.fi-error.fi-focused {
    box-shadow: 0 0 0 3px var(--color-error-light);
  }

  .fi-wrap.fi-success {
    border-color: var(--color-success);
  }

  .fi-wrap.fi-disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  /* ── Focus accent bar ── */
  .fi-accent {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 2px;
    width: 0;
    background: var(--color-brand-primary);
    transition: width 300ms cubic-bezier(0.16,1,0.3,1);
    z-index: 1;
  }
  .fi-wrap.fi-focused .fi-accent {
    width: 100%;
  }
  .fi-wrap.fi-error .fi-accent {
    background: var(--color-error);
  }
  .fi-wrap.fi-success .fi-accent {
    background: var(--color-success);
  }

  /* ── Icons ── */
  .fi-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-text-muted);
    transition: color 200ms ease;
    flex-shrink: 0;
  }

  .fi-icon-left {
    margin-left: 12px;
  }
  .fi-icon-inner {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 3px;
    background: transparent;
    transition: background 200ms ease, color 200ms ease;
  }

  .fi-wrap.fi-focused .fi-icon-left .fi-icon-inner {
    background: var(--color-brand-glow, rgba(139,0,0,0.12));
    color: var(--color-brand-primary);
  }

  [data-theme="light"] .fi-wrap.fi-focused .fi-icon-left .fi-icon-inner {
    background: rgba(26,95,122,0.1);
    color: var(--color-brand-primary);
  }

  .fi-icon-right {
    margin-right: 12px;
  }

  .fi-status-error {
    color: var(--color-error);
    animation: fi-status-pop 250ms cubic-bezier(0.34,1.56,0.64,1);
  }
  .fi-status-success {
    color: var(--color-success);
    animation: fi-status-pop 250ms cubic-bezier(0.34,1.56,0.64,1);
  }

  @keyframes fi-status-pop {
    0% { transform: scale(0); }
    60% { transform: scale(1.2); }
    100% { transform: scale(1); }
  }

  /* ── Input ── */
  .fi-input {
    flex: 1;
    width: 100%;
    height: 44px;
    padding: 0 14px;
    background: transparent;
    border: none;
    outline: none;
    font-family: var(--font-body);
    font-size: 14px;
    color: var(--color-text-primary);
  }

  .fi-input.fi-has-left {
    padding-left: 8px;
  }

  .fi-input::placeholder {
    color: var(--color-text-muted);
    transition: color 200ms ease;
  }
  .fi-wrap.fi-focused .fi-input::placeholder {
    color: var(--color-text-secondary);
  }

  .fi-input:disabled {
    cursor: not-allowed;
  }

  /* ── Password toggle ── */
  .fi-pwd-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    margin-right: 8px;
    background: transparent;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    transition: background 150ms ease;
    flex-shrink: 0;
  }
  .fi-pwd-toggle:hover {
    background: var(--color-bg-hover);
  }

  .fi-pwd-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-text-muted);
    transition: color 200ms ease, transform 300ms cubic-bezier(0.16,1,0.3,1);
  }
  .fi-pwd-icon.fi-pwd-visible {
    color: var(--color-brand-primary);
    transform: scale(1.05);
  }

  /* ── Message ── */
  .fi-message {
    font-size: 12px;
    color: var(--color-text-muted);
    transition: color 150ms ease;
    animation: fi-msg-in 200ms cubic-bezier(0.16,1,0.3,1);
  }
  .fi-msg-error {
    color: var(--color-error);
  }

  @keyframes fi-msg-in {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* ── Autofill ── */
  .fi-input:-webkit-autofill,
  .fi-input:-webkit-autofill:hover,
  .fi-input:-webkit-autofill:focus {
    -webkit-text-fill-color: var(--color-text-primary);
    -webkit-box-shadow: 0 0 0px 1000px var(--color-bg-tertiary) inset;
    transition: background-color 5000s ease-in-out 0s;
  }

  /* ── Reduced motion ── */
  @media (prefers-reduced-motion: reduce) {
    .fi-accent,
    .fi-wrap,
    .fi-icon-inner,
    .fi-pwd-icon,
    .fi-status-error,
    .fi-status-success,
    .fi-message {
      animation: none !important;
      transition-duration: 0ms !important;
    }
  }
`;

export default FormInput;
